import { useState, useCallback } from 'react';
import { isBefore, isAfter, isSameDay, startOfDay, endOfDay } from 'date-fns';

export const useDateRange = (initialStartDate = null, initialEndDate = null) => {
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(initialEndDate);
    const [hoverDate, setHoverDate] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const handleDateClick = useCallback((date) => {
        if (!startDate || (startDate && endDate)) {
            setStartDate(startOfDay(date));
            setEndDate(null);
        } else {
            if (isBefore(date, startDate)) {
                setEndDate(endOfDay(startDate));
                setStartDate(startOfDay(date));
            } else {
                setEndDate(endOfDay(date));
            }
        }
    }, [startDate, endDate]);

    const setDateRange = useCallback((start, end) => {
        setStartDate(start ? startOfDay(start) : null);
        setEndDate(end ? endOfDay(end) : null);
    }, []);

    const clearDateRange = useCallback(() => {
        setStartDate(null);
        setEndDate(null);
    }, []);

    const isInRange = useCallback((date) => {
        if (!startDate) return false;

        const compareDate = hoverDate && !endDate ? hoverDate : endDate;
        if (!compareDate) return isSameDay(date, startDate);

        const rangeStart = isBefore(startDate, compareDate) ? startDate : compareDate;
        const rangeEnd = isAfter(startDate, compareDate) ? startDate : compareDate;

        return (
            (isAfter(date, rangeStart) && isBefore(date, rangeEnd)) ||
            isSameDay(date, rangeStart) ||
            isSameDay(date, rangeEnd)
        );
    }, [startDate, endDate, hoverDate]);

    const isRangeStart = useCallback((date) => {
        return startDate && isSameDay(date, startDate);
    }, [startDate]);

    const isRangeEnd = useCallback((date) => {
        const compareDate = hoverDate && !endDate ? hoverDate : endDate;
        return compareDate && isSameDay(date, compareDate);
    }, [endDate, hoverDate]);

    const goToNextMonth = useCallback(() => {
        setCurrentMonth(prev => {
            const next = new Date(prev);
            next.setMonth(next.getMonth() + 1);
            return next;
        });
    }, []);

    const goToPreviousMonth = useCallback(() => {
        setCurrentMonth(prev => {
            const previous = new Date(prev);
            previous.setMonth(previous.getMonth() - 1);
            return previous;
        });
    }, []);

    return {
        startDate,
        endDate,
        hoverDate,
        currentMonth,
        setHoverDate,
        handleDateClick,
        setDateRange,
        clearDateRange,
        isInRange,
        isRangeStart,
        isRangeEnd,
        goToNextMonth,
        goToPreviousMonth
    };
};
